/**
 * Jeder API-Pfad, den das Admin-UI benutzt — genau einmal, an einer Stelle.
 *
 * Der Zweck ist nicht Bequemlichkeit, sondern Pruefbarkeit: der Contract-Test
 * `packages/api/tests/ui-routes.test.ts` liest diese Datei im Klartext, haengt
 * die Methode vom jeweiligen Aufruf daran und setzt beides gegen die echte
 * Fastify-Routentabelle. Er wird rot, sobald das UI eine Route aufruft, die es
 * im Backend nicht gibt (Anlass: `PATCH /api/v1/me`, vom UI benutzt, bevor die
 * Route existierte — MSW im Admin-Test hatte sie einfach mitgemockt).
 *
 * Damit der Test die Datei lesen kann, gelten drei Formregeln:
 *
 *   1. Ein Eintrag je Endpunkt; der Wert ist ein String oder ein
 *      Pfeil-Ausdruck mit **einem** Template-Literal, das ohne Zeilenumbruch
 *      geschrieben ist.
 *   2. Der Pfad steht ausgeschrieben da — kein `base(tenantId)`-Helfer, keine
 *      Verzweigung. Jedes `${…}` ist genau ein Pfadsegment.
 *   3. **Keine Query-Strings.** Die haengt der Aufrufer an; der Test prueft,
 *      dass hier kein `?` steht.
 *
 * Der Praefix `/api` steht in `API_BASE` und gehoert nicht in die Eintraege.
 */

/** Relativ: im Dev proxyt Vite `/api` an die API, produktiv liegt beides hinter Nginx. */
export const API_BASE = '/api';

/**
 * Better-Auth bedient sich selbst unter diesem Praefix (Login, 2FA,
 * Passwortwechsel, Adresswechsel). Die API registriert dafuer **eine**
 * Wildcard-Route; der Contract-Test prueft die Familie, nicht die einzelnen
 * Unterpfade — die kennt nur Better-Auth.
 */
export const AUTH_BASE = '/api/auth';

export const apiPaths = {
  // --- Plattform (ohne Mandant) ---
  csrf: '/v1/csrf',
  me: '/v1/me',
  /** Anlegen (nur Plattform-Admin); die Liste haengt an der Session. */
  tenants: '/v1/tenants',
  tenant: (tenantId: string) => `/v1/tenants/${tenantId}`,
  /** Betreiber-Einstellungen (ADR 0014) — nur fuer Plattform-Admins. */
  platformSystemMail: '/v1/platform/system-mail',
  platformSystemMailTest: '/v1/platform/system-mail/test',
  /** Betreiber-Sicht auf alle Mandanten, mit Mitglieder-Zahl. */
  platformTenants: '/v1/platform/tenants',
  /** Verbindungs-Vault (ADR 0017) — Zugaenge zu fremden Diensten. */
  platformConnections: '/v1/platform/connections',
  platformConnection: (connectionId: string) => `/v1/platform/connections/${connectionId}`,
  platformConnectionTest: (connectionId: string) =>
    `/v1/platform/connections/${connectionId}/test`,

  // --- Mitglieder, Teams, Protokoll ---
  members: (tenantId: string) => `/v1/tenants/${tenantId}/members`,
  memberNotificationPrefs: (tenantId: string, memberId: string) =>
    `/v1/tenants/${tenantId}/members/${memberId}/notification-prefs`,
  teams: (tenantId: string) => `/v1/tenants/${tenantId}/teams`,
  team: (tenantId: string, teamId: string) => `/v1/tenants/${tenantId}/teams/${teamId}`,
  teamMembers: (tenantId: string, teamId: string) =>
    `/v1/tenants/${tenantId}/teams/${teamId}/members`,
  auditLog: (tenantId: string) => `/v1/tenants/${tenantId}/audit-log`,
  /** WebSocket (Live-Updates + Presence) — Fastify registriert die Route als GET. */
  events: (tenantId: string) => `/v1/tenants/${tenantId}/events`,

  // --- Tickets ---
  tickets: (tenantId: string) => `/v1/tenants/${tenantId}/tickets`,
  ticket: (tenantId: string, ticketId: string) => `/v1/tenants/${tenantId}/tickets/${ticketId}`,
  ticketStatus: (tenantId: string, ticketId: string) =>
    `/v1/tenants/${tenantId}/tickets/${ticketId}/status`,
  ticketTags: (tenantId: string, ticketId: string) =>
    `/v1/tenants/${tenantId}/tickets/${ticketId}/tags`,
  ticketMessages: (tenantId: string, ticketId: string) =>
    `/v1/tenants/${tenantId}/tickets/${ticketId}/messages`,
  ticketMerge: (tenantId: string, ticketId: string) =>
    `/v1/tenants/${tenantId}/tickets/${ticketId}/merge`,
  ticketPresence: (tenantId: string, ticketId: string) =>
    `/v1/tenants/${tenantId}/tickets/${ticketId}/presence`,

  // --- Makros ---
  macros: (tenantId: string) => `/v1/tenants/${tenantId}/macros`,
  macro: (tenantId: string, macroId: string) => `/v1/tenants/${tenantId}/macros/${macroId}`,
  ticketMacro: (tenantId: string, ticketId: string, macroId: string) =>
    `/v1/tenants/${tenantId}/tickets/${ticketId}/macros/${macroId}`,

  // --- CSAT ---
  csatSummary: (tenantId: string) => `/v1/tenants/${tenantId}/csat/summary`,
  ticketCsat: (tenantId: string, ticketId: string) =>
    `/v1/tenants/${tenantId}/tickets/${ticketId}/csat`,
  /** Oeffentlich, ohne Login — der Token steht im Mail-Link. */
  publicCsat: (token: string) => `/v1/public/csat/${encodeURIComponent(token)}`,

  // --- SkillLock ---
  skilllockSettings: (tenantId: string) => `/v1/tenants/${tenantId}/skilllock/settings`,
  skilllockWikiSearch: (tenantId: string) => `/v1/tenants/${tenantId}/skilllock/wiki/search`,
  ticketSkilllockTasks: (tenantId: string, ticketId: string) =>
    `/v1/tenants/${tenantId}/tickets/${ticketId}/skilllock/tasks`,

  /** Freigegebene Verbindungen des Mandanten — nur Name, Typ, Provider. */
  connections: (tenantId: string) => `/v1/tenants/${tenantId}/connections`,

  // --- Queues und E-Mail ---
  queues: (tenantId: string) => `/v1/tenants/${tenantId}/queues`,
  queue: (tenantId: string, queueId: string) => `/v1/tenants/${tenantId}/queues/${queueId}`,
  queueEmail: (tenantId: string, queueId: string) =>
    `/v1/tenants/${tenantId}/queues/${queueId}/email`,
  queueEmailTest: (tenantId: string, queueId: string) =>
    `/v1/tenants/${tenantId}/queues/${queueId}/email/test`,
  emailLog: (tenantId: string) => `/v1/tenants/${tenantId}/email-log`,
  emailLogProcess: (tenantId: string, entryId: string) =>
    `/v1/tenants/${tenantId}/email-log/${entryId}/process`,
  emailLogLearn: (tenantId: string, entryId: string) =>
    `/v1/tenants/${tenantId}/email-log/${entryId}/learn`,

  // --- Hintergrund-Jobs ---
  failedJobs: (tenantId: string) => `/v1/tenants/${tenantId}/jobs/failed`,
  failedJob: (tenantId: string, queue: string, jobId: string) =>
    `/v1/tenants/${tenantId}/jobs/failed/${encodeURIComponent(queue)}/${encodeURIComponent(jobId)}`,
  failedJobRetry: (tenantId: string, queue: string, jobId: string) =>
    `/v1/tenants/${tenantId}/jobs/failed/${encodeURIComponent(queue)}/${encodeURIComponent(jobId)}/retry`,

  // --- Quellen und Rueckkanal ---
  sources: (tenantId: string) => `/v1/tenants/${tenantId}/sources`,
  source: (tenantId: string, sourceId: string) => `/v1/tenants/${tenantId}/sources/${sourceId}`,
  sourceApiKey: (tenantId: string, sourceId: string) =>
    `/v1/tenants/${tenantId}/sources/${sourceId}/api-key`,
  sourceWebhookSecret: (tenantId: string, sourceId: string) =>
    `/v1/tenants/${tenantId}/sources/${sourceId}/webhook-secret`,
  webhookDeliveries: (tenantId: string) => `/v1/tenants/${tenantId}/webhook-deliveries`,
  webhookDeliveryRetry: (tenantId: string, deliveryId: string) =>
    `/v1/tenants/${tenantId}/webhook-deliveries/${deliveryId}/retry`,

  // --- Tags, Textbausteine, Kontakte ---
  tags: (tenantId: string) => `/v1/tenants/${tenantId}/tags`,
  snippets: (tenantId: string) => `/v1/tenants/${tenantId}/snippets`,
  snippet: (tenantId: string, snippetId: string) =>
    `/v1/tenants/${tenantId}/snippets/${snippetId}`,
  contacts: (tenantId: string) => `/v1/tenants/${tenantId}/contacts`,
  contact: (tenantId: string, contactId: string) =>
    `/v1/tenants/${tenantId}/contacts/${contactId}`,

  // --- Anhaenge ---
  allowedFileTypes: (tenantId: string) => `/v1/tenants/${tenantId}/allowed-file-types`,
  attachments: (tenantId: string) => `/v1/tenants/${tenantId}/attachments`,

  // --- KI (Spec 9) ---
  aiSettings: (tenantId: string) => `/v1/tenants/${tenantId}/ai/settings`,
  ticketAiDrafts: (tenantId: string, ticketId: string) =>
    `/v1/tenants/${tenantId}/tickets/${ticketId}/ai/drafts`,
  aiDraft: (tenantId: string, draftId: string) => `/v1/tenants/${tenantId}/ai/drafts/${draftId}`,
  ticketAiSuggestions: (tenantId: string, ticketId: string) =>
    `/v1/tenants/${tenantId}/tickets/${ticketId}/ai/suggestions`,
  aiSuggestionAccept: (tenantId: string, suggestionId: string) =>
    `/v1/tenants/${tenantId}/ai/suggestions/${suggestionId}/accept`,
  aiSuggestionReject: (tenantId: string, suggestionId: string) =>
    `/v1/tenants/${tenantId}/ai/suggestions/${suggestionId}/reject`,
  ticketAiSimilar: (tenantId: string, ticketId: string) =>
    `/v1/tenants/${tenantId}/tickets/${ticketId}/ai/similar`,
  aiKb: (tenantId: string) => `/v1/tenants/${tenantId}/ai/kb`,
  aiKbArticle: (tenantId: string, articleId: string) =>
    `/v1/tenants/${tenantId}/ai/kb/${articleId}`,
  ticketAiKbDraft: (tenantId: string, ticketId: string) =>
    `/v1/tenants/${tenantId}/tickets/${ticketId}/ai/kb-draft`,
};
